import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import Loader from "../../../Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  useGetSupplierByIdQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useGetSuppliersQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface SupplierPayload {
  name: string;
  registration_number?: string;
  tax_id?: string;
  contact_name?: string;
  contact_email: string;
  optional_emails: string;
  contact_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  website?: string;
  business_type?: string;
  credit_term?: string;
  credit_limit?: number;
  currency_code?: string;
  tax?: string;
}

interface SupplierEditProps {
  supplierId?: string | number;
  compact?: boolean; // when true, omit breadcrumb/page wrapper (for modal)
  onSaved?: () => void; // optional callback parent can use to close modal / refresh
  onCancel?: () => void;
}

const SupplierEditView: React.FC<SupplierEditProps> = ({
  supplierId,
  compact = true,
  onSaved,
  onCancel,
}) => {
  const isEditMode = supplierId !== undefined && supplierId !== null;
  const supplierIdStr = isEditMode ? String(supplierId) : undefined;

  const [saveError, setSaveError] = useState<string | null>(null);

  // fetch supplier detail when editing
  const {
    data: supplierData,
    isFetching: fetchingDetail,
    refetch: refetchSupplier,
  } = useGetSupplierByIdQuery(supplierIdStr as string, {
    skip: !isEditMode,
  });

  // to refresh list after save
  const { refetch: refetchSuppliers } = useGetSuppliersQuery(undefined);

  const [createSupplier, { isLoading: creating }] = useCreateSupplierMutation();
  const [updateSupplier, { isLoading: updating }] = useUpdateSupplierMutation();

  const formik = useFormik<SupplierPayload>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      registration_number: "",
      tax_id: "",
      contact_name: "",
      contact_email: "",
      optional_emails: "",
      contact_phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      website: "",
      business_type: "",
      credit_term: "",
      credit_limit: undefined,
      currency_code: "",
      tax: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter supplier name"),
      contact_email: Yup.string()
        .email("Please enter a valid email")
        .required("Contact email is required"),
      optional_emails: Yup.string().test(
        "optional-emails",
        "Enter valid, unique email addresses separated by commas",
        function (value) {
          const emails = (value || "")
            .split(",")
            .map((email) => email.trim().toLowerCase())
            .filter(Boolean);
          const primaryEmail = (this.parent.contact_email || "").trim().toLowerCase();
          return (
            emails.every((email) => Yup.string().email().isValidSync(email)) &&
            emails.length === new Set(emails).size &&
            !emails.includes(primaryEmail)
          );
        }
      ),
      registration_number: Yup.string().nullable(),
      tax_id: Yup.string().nullable(),
      contact_name: Yup.string().nullable(),
      contact_phone: Yup.string().nullable(),
      address: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      country: Yup.string().nullable(),
      postal_code: Yup.string().nullable(),
      website: Yup.string().url("Please enter a valid URL").nullable(),
      business_type: Yup.string().nullable(),
      credit_term: Yup.string().nullable(),
      credit_limit: Yup.number().min(0, "Credit limit must be 0 or greater").nullable(),
      currency_code: Yup.string().nullable(),
      tax: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      setSaveError(null);
      try {
        const payload = {
          ...values,
          optional_emails: values.optional_emails
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean),
        };
        if (isEditMode && supplierId !== undefined) {
          // send shape the mutation expects: { id, data }
          const idNumber = Number(supplierId);
          const idToSend = Number.isNaN(idNumber) ? supplierIdStr : idNumber;

          await updateSupplier({ id: idToSend, values: payload }).unwrap();
        } else {
          await createSupplier(payload).unwrap();
        }

        // refresh list and single item
        refetchSuppliers();
        if (isEditMode) refetchSupplier();

        // notify parent so it can close modal / refresh
        if (onSaved) onSaved();
      } catch (err: any) {
        const message =
          err?.data?.message || err?.message || "Failed to save supplier";
        console.error("Save failed:", err);
        setSaveError(String(message));
      }
    },
  });

  // populate form when editing
  useEffect(() => {
    if (isEditMode && supplierData) {
      formik.setValues({
        name: supplierData.name ?? "",
        registration_number: supplierData.registration_number ?? "",
        tax_id: supplierData.tax_id ?? "",
        contact_name: supplierData.contact_name ?? "",
        contact_email: supplierData.contact_email ?? "",
        optional_emails: (supplierData.optional_emails ?? []).join(", "),
        contact_phone: supplierData.contact_phone ?? "",
        address: supplierData.address ?? "",
        city: supplierData.city ?? "",
        state: supplierData.state ?? "",
        country: supplierData.country ?? "",
        postal_code: supplierData.postal_code ?? "",
        website: supplierData.website ?? "",
        business_type: supplierData.business_type ?? "",
        credit_term: supplierData.credit_term ?? "",
        credit_limit: supplierData.credit_limit ?? undefined,
        currency_code: supplierData.currency_code ?? "",
        tax: supplierData.tax ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierData, isEditMode]);

  // show loader if editing and the single item is loading (compact = modal)
  if (isEditMode && fetchingDetail && compact) {
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {saveError && (
        <Row>
          <Col lg={12}>
            <Alert color="danger">{saveError}</Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            {!compact && (
              <CardHeader>
                <h5 className="card-title mb-0">
                  {isEditMode ? "Edit Supplier" : "Add Supplier"}
                </h5>
              </CardHeader>
            )}

            <CardBody>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-name">
                        Supplier Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="supplier-name"
                        name="name"
                        placeholder="e.g. ABC Corporation"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={!!(formik.touched.name && formik.errors.name)}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-1">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-business-type"
                      >
                        Business Type
                      </Label>
                      <Input
                        id="supplier-business-type"
                        name="business_type"
                        placeholder="e.g. Manufacturer, Distributor"
                        value={formik.values.business_type}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-optional-emails">
                        Optional Emails
                      </Label>
                      <Input
                        id="supplier-optional-emails"
                        name="optional_emails"
                        placeholder="sales@company.com, accounts@company.com"
                        value={formik.values.optional_emails}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={!!(formik.touched.optional_emails && formik.errors.optional_emails)}
                      />
                      <div className="form-text">Separate multiple email addresses with commas.</div>
                      {formik.touched.optional_emails && formik.errors.optional_emails ? (
                        <div className="text-danger mt-1">{formik.errors.optional_emails}</div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-registration-number"
                      >
                        Registration Number
                      </Label>
                      <Input
                        id="supplier-registration-number"
                        name="registration_number"
                        placeholder="Company registration number"
                        value={formik.values.registration_number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-tax-id">
                        Tax ID
                      </Label>
                      <Input
                        id="supplier-tax-id"
                        name="tax_id"
                        placeholder="Tax identification number"
                        value={formik.values.tax_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-website">
                        Website
                      </Label>
                      <Input
                        type="url"
                        id="supplier-website"
                        name="website"
                        placeholder="https://example.com"
                        value={formik.values.website}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          !!(formik.touched.website && formik.errors.website)
                        }
                      />
                      {formik.touched.website && formik.errors.website ? (
                        <div className="text-danger mt-1">
                          {formik.errors.website}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={6}></Col>
                </Row>

                <h6 className="mb-3">Contact Information</h6>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-contact-name"
                      >
                        Contact Name
                      </Label>
                      <Input
                        id="supplier-contact-name"
                        name="contact_name"
                        placeholder="Primary contact person"
                        value={formik.values.contact_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-contact-email"
                      >
                        Contact Email <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="email"
                        id="supplier-contact-email"
                        name="contact_email"
                        placeholder="contact@company.com"
                        value={formik.values.contact_email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          !!(
                            formik.touched.contact_email &&
                            formik.errors.contact_email
                          )
                        }
                      />
                      {formik.touched.contact_email &&
                        formik.errors.contact_email ? (
                        <div className="text-danger mt-1">
                          {formik.errors.contact_email}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-contact-phone"
                      >
                        Contact Phone
                      </Label>
                      <Input
                        id="supplier-contact-phone"
                        name="contact_phone"
                        placeholder="Phone number"
                        value={formik.values.contact_phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                </Row>

                <h6 className="mb-3">Financial Information</h6>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-credit-term"
                      >
                        Credit Term
                      </Label>
                      <Input
                        type="select"
                        id="supplier-credit-term"
                        name="credit_term"
                        value={formik.values.credit_term}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        <option value="">Select Credit Term</option>
                        <option value="07 DAYS">07 DAYS</option>
                        <option value="AS PER P.O">AS PER P.O</option>
                        <option value="COD">COD</option>
                      </Input>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-credit-limit"
                      >
                        Credit Limit
                      </Label>
                      <Input
                        type="number"
                        id="supplier-credit-limit"
                        name="credit_limit"
                        placeholder="e.g. 30000"
                        value={formik.values.credit_limit || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          !!(
                            formik.touched.credit_limit &&
                            formik.errors.credit_limit
                          )
                        }
                      />
                      {formik.touched.credit_limit &&
                        formik.errors.credit_limit ? (
                        <div className="text-danger mt-1">
                          {formik.errors.credit_limit}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label
                        className="form-label"
                        htmlFor="supplier-currency-code"
                      >
                        Currency Code
                      </Label>
                      <Input
                        id="supplier-currency-code"
                        name="currency_code"
                        placeholder="e.g. SGD, USD, INR"
                        value={formik.values.currency_code}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-tax">
                        Tax
                      </Label>
                      <Input
                        id="supplier-tax"
                        name="tax"
                        placeholder="Tax information"
                        value={formik.values.tax}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                </Row>

                <h6 className="mb-3">Address Information</h6>
                <div className="mb-3">
                  <Label className="form-label" htmlFor="supplier-address">
                    Address
                  </Label>
                  <Input
                    id="supplier-address"
                    name="address"
                    placeholder="Street address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <Row>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-city">
                        City
                      </Label>
                      <Input
                        id="supplier-city"
                        name="city"
                        placeholder="City"
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-state">
                        State/Province
                      </Label>
                      <Input
                        id="supplier-state"
                        name="state"
                        placeholder="State or province"
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="supplier-country">
                        Country
                      </Label>
                      <Input
                        id="supplier-country"
                        name="country"
                        placeholder="Country"
                        value={formik.values.country}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </Col>
                </Row>

                <div className="mb-3">
                  <Label className="form-label" htmlFor="supplier-postal-code">
                    Postal Code
                  </Label>
                  <Input
                    id="supplier-postal-code"
                    name="postal_code"
                    placeholder="Postal code"
                    value={formik.values.postal_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="text-end">
                  {onCancel && (
                    <Button
                      type="button"
                      color="secondary"
                      className="me-2"
                      onClick={onCancel}
                      disabled={creating || updating}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    color="primary"
                    type="submit"
                    disabled={creating || updating}
                  >
                    {creating || updating ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Saving...
                      </>
                    ) : isEditMode ? (
                      "Update Supplier"
                    ) : (
                      "Create Supplier"
                    )}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SupplierEditView;
